/**
 * 現場アドレス帳（A-07 の中・**FR-129/D-86**）＝**工務店グループで共有する自分たちの現場**。
 *
 * ★**卸のマスタとは別物**＝ここに登録しても卸の現場マスタは書き換わらない（プレフィル方式）。
 *   注文のときは名前だけが卸へ渡り、卸のマスタに同じ名前があればそこに結び付く（S2）。
 *   だから「まだ卸に登録していない現場」でも、こちらで作っておけば毎回打ち直さずに済む。
 * ★**削除しない**（規約6）＝終わった現場は「しまう」（archived）。過去の注文が指している
 *   来歴（`orders.builder_site_id`）は残る。
 * ★**重複サジェスト**＝同じ現場が「〇〇邸」「〇〇邸 新築工事」と2つできると、共有している
 *   メンバーごとに違う現場を選び、卸から見ると別の現場に見える。登録の前に気づける形にする。
 * ★PATCH は**全項目の置き換え**（backend の `BuilderSitePatch`）＝既定にする・しまう だけの
 *   操作でも現在の値を全部送る。1項目だけ送ると住所と連絡先が消える。
 * ★**M1.9b（Design 忠実化）＝1件を `.addr-card` の3〜4行**に畳んだ（住所にピン・備考に memo と
 *   警告色）。名前・語彙・操作・testid は変えていない＝見た目だけの変更。
 */

import type { BuilderSite, BuilderSiteInput, partnerApi } from "@kenzai/api-client";
import { Badge, ConfirmDialog, ErrorNote, Spinner } from "@kenzai/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Input } from "./formKit";
import { useIsDesktop } from "../useIsDesktop";

type Client = ReturnType<typeof partnerApi>;

/**
 * 小さなインライン SVG（Design `order-app.js` の `ICO` と同じ図形）。
 * **アイコンのライブラリは足さない**（Shell の `TabIcon` と同じ方針）。プロフィール系の3画面で
 * 使うので、この束だけここから配る（`SmallButton` と同じ扱い）。
 */
const ICON_PATHS: Record<string, JSX.Element> = {
  pin: (
    <>
      <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  memo: (
    <>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6" />
    </>
  ),
  edit: <path d="M17 3a2.8 2.8 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5z" />,
  chevR: <polyline points="9 6 15 12 9 18" />,
  store: (
    <>
      <path d="M4 4h16l1 5a3 3 0 0 1-3 3 3 3 0 0 1-3-3 3 3 0 0 1-6 0 3 3 0 0 1-3 3 3 3 0 0 1-3-3z" />
      <path d="M5 12v8h14v-8" />
      <path d="M9 20v-5h6v5" />
    </>
  ),
  plusc: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v8M8 12h8" />
    </>
  ),
};

export function Ico({
  name,
  size = 14,
  strokeWidth = 1.9,
}: {
  name: keyof typeof ICON_PATHS;
  size?: number;
  strokeWidth?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

/** 名寄せ判定の正規化（前後空白・全角空白・大小・連続空白）。**判定はここだけ**。 */
export function normalizeSiteName(s: string): string {
  return s.replace(/\u3000/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
}

/** 「この現場ではありませんか」の候補（完全一致・部分一致の両方向）。 */
function similarSites(name: string, sites: BuilderSite[]): BuilderSite[] {
  const n = normalizeSiteName(name);
  if (n.length < 2) return [];
  return sites.filter((s) => {
    const t = normalizeSiteName(s.name);
    return t === n || t.includes(n) || n.includes(t);
  });
}

const EMPTY: BuilderSiteInput = { name: "", address: "", contact_person: "", phone: "", note: "" };

export function AddressBook({ client }: { client: Client }) {
  const qc = useQueryClient();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<BuilderSite | null>(null);
  // PC は Design の表・スマホはカード（`book-site-{id}` を二重に出さないため排他）
  const isDesktop = useIsDesktop();

  const q = useQuery({
    queryKey: ["builder-sites", includeArchived],
    queryFn: () => client.builderSites(includeArchived),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["builder-sites"] });

  const save = useMutation({
    mutationFn: ({ id, body }: { id: number | null; body: BuilderSiteInput & { status?: "active" | "archived" } }) =>
      id === null ? client.createBuilderSite(body) : client.patchBuilderSite(id, body),
    onSuccess: async () => {
      setAdding(false);
      setEditingId(null);
      setConfirm(null);
      await invalidate();
    },
  });

  if (q.isLoading) return <Spinner />;

  const sites = q.data ?? [];
  const active = sites.filter((s) => s.status === "active");

  /** 既定にする／しまう／戻す＝**現在値を全部送る**（PATCH は置き換え）。 */
  const patch = (s: BuilderSite, over: Partial<BuilderSiteInput & { status: "active" | "archived" }>) =>
    save.mutate({
      id: s.id,
      body: {
        name: s.name,
        address: s.address,
        contact_person: s.contact_person,
        phone: s.phone,
        note: s.note,
        is_default: s.is_default,
        status: s.status === "archived" ? "archived" : "active",
        ...over,
      },
    });

  return (
    <section>
      {/* 見出しの右肩に「追加」を置く（Design `.pf-sec`）＝一覧が長くても入口が上にある。 */}
      <div className="pf-sec">
        <b>現場（お届け先）</b>
        {!adding && (
          <SmallButton testId="book-add" onClick={() => setAdding(true)}>
            ＋ 現場を追加
          </SmallButton>
        )}
      </div>
      <p className="oa-lead">ご注文のときにここから選べます。登録した現場は、同じ工務店グループの方にも見えます。</p>

      {q.isError && <ErrorNote error={q.error} />}
      {save.isError && <ErrorNote error={save.error} />}

      {sites.length === 0 && !adding && (
        <p style={{ margin: "0 0 var(--kz-space-3)", fontSize: "var(--kz-font-size-sm)" }}>
          まだ現場が登録されていません。
        </p>
      )}

      <div
        data-testid="site-book"
        style={isDesktop ? undefined : { display: "grid", gap: "var(--kz-space-2)" }}
      >
        {/* ★PC＝Design のアドレス帳テーブル（現場名／住所／宛名／電話／備考／操作）。
            スマホはカード（`.addr-card`）＝**排他レンダリング**（`book-site-{id}` を二重に
            出さない）。Design の「原価集計先」列は作らない＝現場は卸のマスタに直結せず
            （プレフィル方式・D-86）、原価は**現場名の一致で自動的に**集計される＝
            選ばせる欄が存在しない。そのことは下の注記で言う。 */}
        {isDesktop ? (
          <div className="kp-tblwrap">
            <table className="kp-tbl">
              <thead>
                <tr>
                  <th>現場（お届け先）</th>
                  <th>住所</th>
                  <th>宛名</th>
                  <th>電話番号</th>
                  <th>備考（置き場所メモ）</th>
                  <th aria-label="操作" />
                </tr>
              </thead>
              <tbody>
                {sites.map((s) =>
                  editingId === s.id ? (
                    <tr key={s.id}>
                      <td colSpan={6}>
                        <SiteForm
                          testIdPrefix="site-edit"
                          initial={s}
                          others={active.filter((x) => x.id !== s.id)}
                          busy={save.isPending}
                          onCancel={() => setEditingId(null)}
                          onSubmit={(body) =>
                            save.mutate({
                              id: s.id,
                              body: { ...body, status: s.status === "archived" ? "archived" : "active" },
                            })
                          }
                        />
                      </td>
                    </tr>
                  ) : (
                    <tr
                      key={s.id}
                      data-testid={`book-site-${s.id}`}
                      style={{ opacity: s.status === "archived" ? 0.6 : 1 }}
                    >
                      <td>
                        <b>{s.name}</b>
                        {s.is_default && <em className="kp-def">よく使う</em>}
                        {s.status === "archived" && <span className="sub">しまってあります</span>}
                      </td>
                      <td>{s.address || "—"}</td>
                      <td>{s.contact_person || "—"}</td>
                      <td className="kz-num">{s.phone || "—"}</td>
                      {/* 備考は読み落とすと配送が止まる（「17時以降は搬入不可」等）＝警告色のまま */}
                      <td style={{ color: s.note ? "var(--kz-status-warn)" : undefined }}>{s.note || "—"}</td>
                      <td>
                        <span className="kp-acts">
                          <button
                            type="button"
                            className="kp-act"
                            data-testid={`book-edit-${s.id}`}
                            onClick={() => setEditingId(s.id)}
                          >
                            <Ico name="edit" size={13} />
                            編集
                          </button>
                          {s.status === "active" && !s.is_default && (
                            <button
                              type="button"
                              className="kp-act"
                              data-testid={`book-default-${s.id}`}
                              onClick={() => patch(s, { is_default: true })}
                            >
                              よく使う現場にする
                            </button>
                          )}
                          {s.status === "active" ? (
                            <button
                              type="button"
                              className="kp-act"
                              data-testid={`book-archive-${s.id}`}
                              onClick={() => setConfirm(s)}
                            >
                              しまう
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="kp-act"
                              data-testid={`book-restore-${s.id}`}
                              onClick={() => patch(s, { status: "active" })}
                            >
                              戻す
                            </button>
                          )}
                        </span>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        ) : (
          sites.map((s) =>
          editingId === s.id ? (
            <SiteForm
              key={s.id}
              testIdPrefix="site-edit"
              initial={s}
              others={active.filter((x) => x.id !== s.id)}
              busy={save.isPending}
              onCancel={() => setEditingId(null)}
              onSubmit={(body) => save.mutate({ id: s.id, body: { ...body, status: s.status === "archived" ? "archived" : "active" } })}
            />
          ) : (
            <div
              key={s.id}
              data-testid={`book-site-${s.id}`}
              className="addr-card"
              style={{ opacity: s.status === "archived" ? 0.6 : 1 }}
            >
              <div className="addr-h">
                <b style={{ minWidth: 0, overflowWrap: "anywhere" }}>{s.name}</b>
                {/* 語は現行のまま（「既定」は使わない）＝ボタン「よく使う現場にする」と揃える。 */}
                {s.is_default && <em className="addr-def">よく使う</em>}
                {s.status === "archived" && <Badge>しまってあります</Badge>}
                <button
                  type="button"
                  className="addr-edit kz-pressable"
                  data-testid={`book-edit-${s.id}`}
                  onClick={() => setEditingId(s.id)}
                  // ブラウザ既定の枠と地色を消す（Design は文字だけのボタン）。
                  // 押せる面 44px はクラス側が持っているので寸法には触らない。
                  style={{ background: "none", border: 0, font: "inherit", cursor: "pointer" }}
                >
                  <Ico name="edit" />
                  編集
                </button>
              </div>
              {s.address && (
                <div className="addr-l">
                  <Ico name="pin" />
                  <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{s.address}</span>
                </div>
              )}
              {(s.contact_person || s.phone) && (
                <div className="addr-l">
                  <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                    {s.contact_person && `宛名：${s.contact_person}`}
                    {s.contact_person && s.phone && "　"}
                    {s.phone}
                  </span>
                </div>
              )}
              {s.note && (
                // 備考は**警告色**＝「17時以降は搬入不可」のような、読み落とすと配送が止まる情報が入る
                <div className="addr-l note">
                  <Ico name="memo" size={13} />
                  <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{s.note}</span>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  gap: "var(--kz-space-2)",
                  flexWrap: "wrap",
                  marginTop: "var(--kz-space-2)",
                }}
              >
                {s.status === "active" && !s.is_default && (
                  <SmallButton testId={`book-default-${s.id}`} onClick={() => patch(s, { is_default: true })}>
                    よく使う現場にする
                  </SmallButton>
                )}
                {s.status === "active" ? (
                  <SmallButton testId={`book-archive-${s.id}`} onClick={() => setConfirm(s)}>
                    しまう
                  </SmallButton>
                ) : (
                  <SmallButton testId={`book-restore-${s.id}`} onClick={() => patch(s, { status: "active" })}>
                    戻す
                  </SmallButton>
                )}
              </div>
            </div>
          ),
          )
        )}
      </div>

      {/* ★現場名がそのまま原価の集計キーになる（D-86＝プレフィル方式で卸のマスタは汚さない）。
          「どこに集計するか」を選ぶ欄が無い理由でもあるので、ここで言っておく。 */}
      <p className="oa-foot">
        備考は「どこに置いておいて欲しい」などのメモとして、ご注文と一緒にお取引先へ伝わります。
        ここの現場名は「現場・原価管理」に自動で集計されます。
      </p>

      {adding && (
        <div style={{ marginTop: "var(--kz-space-3)" }}>
          <SiteForm
            testIdPrefix="site-new"
            initial={EMPTY}
            others={active}
            busy={save.isPending}
            onCancel={() => setAdding(false)}
            onSubmit={(body) => save.mutate({ id: null, body })}
          />
        </div>
      )}

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          minHeight: 44,
          marginTop: "var(--kz-space-2)",
          fontSize: "var(--kz-font-size-sm)",
          color: "var(--kz-color-ink-muted)",
        }}
      >
        <input
          type="checkbox"
          data-testid="book-include-archived"
          checked={includeArchived}
          onChange={(e) => setIncludeArchived(e.target.checked)}
          style={{ width: 20, height: 20 }}
        />
        しまった現場も表示
      </label>

      {confirm && (
        <ConfirmDialog
          title="この現場をしまいますか？"
          body={
            <p style={{ margin: 0 }}>
              {confirm.name} を一覧から外します。過去のご注文は残ります。あとから「戻す」でまた使えます。
            </p>
          }
          confirmLabel="しまう"
          busy={save.isPending}
          onConfirm={() => patch(confirm, { status: "archived", is_default: false })}
          onCancel={() => setConfirm(null)}
        />
      )}
    </section>
  );
}

function SiteForm({
  testIdPrefix,
  initial,
  others,
  busy,
  onSubmit,
  onCancel,
}: {
  testIdPrefix: string;
  initial: BuilderSiteInput | BuilderSite;
  /** 重複サジェストの相手（自分自身は除く）。 */
  others: BuilderSite[];
  busy: boolean;
  onSubmit: (body: BuilderSiteInput) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial.name);
  const [address, setAddress] = useState(initial.address ?? "");
  const [person, setPerson] = useState(initial.contact_person ?? "");
  const [phone, setPhone] = useState(initial.phone ?? "");
  const [note, setNote] = useState(initial.note ?? "");
  const [isDefault, setIsDefault] = useState(initial.is_default ?? false);
  const [error, setError] = useState(false);

  const similar = similarSites(name, others);

  return (
    <div
      data-testid={`${testIdPrefix}-form`}
      style={{
        border: "1px solid var(--kz-color-primary)",
        borderRadius: "var(--kz-radius-sm)",
        padding: "var(--kz-space-3)",
      }}
    >
      <Input
        label="現場名（必須）"
        value={name}
        onChange={(v) => {
          setName(v);
          if (v.trim() !== "") setError(false);
        }}
        maxLength={200}
        required
        data-testid={`${testIdPrefix}-name`}
      />
      {error && (
        <p role="alert" style={{ margin: "0 0 var(--kz-space-2)", color: "var(--kz-status-danger)", fontSize: "var(--kz-font-size-sm)" }}>
          現場名をご入力ください。
        </p>
      )}
      {similar.length > 0 && (
        // 同じ現場を2つ作らせない（共有しているので、メンバーごとに別々を選ぶと卸が混乱する）
        <p
          data-testid={`${testIdPrefix}-dup`}
          style={{
            margin: "0 0 var(--kz-space-2)",
            padding: "var(--kz-space-2)",
            background: "var(--kz-status-warn-weak)",
            borderRadius: "var(--kz-radius-sm)",
            fontSize: "var(--kz-font-size-sm)",
          }}
        >
          この現場ではありませんか？ {similar.map((s) => s.name).join(" ／ ")}
        </p>
      )}
      <Input label="住所" value={address} onChange={setAddress} maxLength={200} data-testid={`${testIdPrefix}-address`} />
      <Input label="現場のご担当者" value={person} onChange={setPerson} maxLength={100} data-testid={`${testIdPrefix}-person`} />
      <Input label="電話番号" value={phone} onChange={setPhone} type="tel" maxLength={30} data-testid={`${testIdPrefix}-phone`} />
      <Input label="メモ" value={note} onChange={setNote} maxLength={500} data-testid={`${testIdPrefix}-note`} />
      <label style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: "var(--kz-space-3)" }}>
        <input
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
          data-testid={`${testIdPrefix}-default`}
          style={{ width: 20, height: 20 }}
        />
        よく使う現場にする（ご注文のときの初期値）
      </label>
      <div style={{ display: "flex", gap: "var(--kz-space-2)" }}>
        <SmallButton
          testId={`${testIdPrefix}-save`}
          primary
          disabled={busy}
          onClick={() => {
            if (name.trim() === "") {
              setError(true);
              return;
            }
            onSubmit({
              name: name.trim(),
              address: address.trim() === "" ? null : address.trim(),
              contact_person: person.trim() === "" ? null : person.trim(),
              phone: phone.trim() === "" ? null : phone.trim(),
              note: note.trim() === "" ? null : note.trim(),
              is_default: isDefault,
            });
          }}
        >
          保存する
        </SmallButton>
        <SmallButton testId={`${testIdPrefix}-cancel`} onClick={onCancel}>
          やめる
        </SmallButton>
      </div>
    </div>
  );
}

export function SmallButton({
  children,
  onClick,
  testId,
  primary = false,
  disabled = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  testId: string;
  primary?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className="kz-pressable"
      style={{
        minHeight: 44,
        padding: "0 var(--kz-space-3)",
        border: primary ? "none" : "1px solid var(--kz-color-border)",
        borderRadius: "var(--kz-radius-sm)",
        background: primary ? "var(--kz-color-primary)" : "var(--kz-color-surface)",
        color: primary ? "var(--kz-color-ink-inverse)" : "inherit",
        font: "inherit",
        fontWeight: primary ? 700 : 400,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {children}
    </button>
  );
}
